import React from "react";
import Segmented from "./Segmented";

const HATCH = "repeating-linear-gradient(45deg,#fff 0 3px,#F1ECE8 3px 6px)";

const Step = ({ label, glyph, to, onPick }) => (
  <button
    type="button"
    aria-label={label}
    disabled={!to}
    onClick={() => onPick(to)}
    className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F26A1F] w-[28px] h-[28px] rounded-[14px] border border-[#E8E0D9] bg-white text-[14px] text-[#3a3a3a] cursor-pointer disabled:cursor-default"
    style={{ opacity: to ? 1 : 0.35 }}
  >
    {glyph}
  </button>
);

/** A row of per-day bars (urgency or straining, 0–3) under the chip columns. */
const BarRow = ({ label, cols, color, has, height, value, tip }) => (
  <>
    <span className="text-[11px] font-semibold text-[#6b6b6b] self-center">{label}</span>
    <div className="flex min-w-0 h-[46px] items-end border-b border-[#F1ECE8]">
      {cols.map((c) => (
        <div key={c.iso} title={c[tip]} className="flex-1 min-w-0 h-full flex flex-col items-center justify-end gap-[2px] px-[2px]">
          {c[has] && <span className="text-[9px] font-bold text-[#5a5a5a] leading-none whitespace-nowrap">{c[value]}</span>}
          <div className="w-full max-w-[28px] rounded-t-[3px]" style={{ height: c[height], background: color }} />
        </div>
      ))}
    </div>
  </>
);

/** One period: its stats, then stool-type chips, urgency and straining bars, incomplete badges and dates per day. */
const Panel = ({ p, onSelect }) => (
  <div className="flex flex-col gap-[14px]" style={{ borderTop: p.bt, paddingTop: p.pt }}>
    <div className="flex items-center gap-[10px] flex-wrap">
      <span
        className="text-[10px] font-bold tracking-[.06em] px-[8px] py-[3px] rounded-[4px] whitespace-nowrap"
        style={{ background: p.tagBg, color: p.tagFg, boxShadow: p.tagRing }}
      >
        {p.tag}
      </span>
      <span className="text-[14px] font-bold">{p.title}</span>
      <span className="text-[12px] text-[#8a8a8a] whitespace-nowrap">{p.range}</span>
    </div>
    <div className="grid grid-cols-3 gap-[10px]">
      {p.stats.map((s) => (
        <div key={s.k} className="bg-[#FBF8F6] rounded-[10px] px-[14px] py-[10px] flex flex-col gap-[2px]">
          <span className="text-[11px] font-semibold text-[#6b6b6b]">{s.k}</span>
          <span className="text-[20px] font-bold tabular-nums">{s.v}</span>
          <span className="text-[11px] font-bold min-h-[14px]" style={{ color: s.dFg }}>
            {s.d}
          </span>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-[10px] gap-y-[4px] items-stretch">
      <span className="text-[11px] font-semibold text-[#6b6b6b] self-end pb-[6px]">Stool type</span>
      <div className="flex min-w-0 border-b border-[#E8E0D9]">
        {p.cols.map((c) => (
          <button
            key={c.iso}
            type="button"
            aria-label={c.label}
            disabled={!c.clickable}
            onClick={() => onSelect(c.iso)}
            className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F26A1F] flex-1 min-w-0 min-h-[96px] flex flex-col justify-end items-center gap-[4px] px-[1px] py-[6px] rounded-t-[6px] cursor-pointer disabled:cursor-default enabled:hover:bg-[#FBF6F2]"
            style={{ background: c.bg, borderLeft: c.bl }}
          >
            {c.chips.map((m, i) => (
              <span
                key={i}
                title={m.tip}
                className="w-full max-w-[36px] h-[18px] rounded-[4px] text-[10px] font-bold flex items-center justify-center"
                style={{ background: m.bg, color: m.fg, boxShadow: m.ring }}
              >
                {m.label}
              </span>
            ))}
            {c.noLog && (
              <span
                title="Not recorded"
                className="w-full max-w-[36px] h-[18px] rounded-[4px]"
                style={{ boxShadow: "inset 0 0 0 1px #CFC4BB", background: HATCH }}
              />
            )}
          </button>
        ))}
      </div>
      <BarRow label="Urgency" cols={p.cols} color="#D99A82" has="hasU" height="uH" value="uV" tip="uTip" />
      <BarRow label="Straining" cols={p.cols} color="#8E4330" has="hasS" height="sH" value="sV" tip="sTip" />
      <span className="text-[11px] font-semibold text-[#6b6b6b] self-center">Incomplete</span>
      <div className="flex min-w-0 h-[24px] items-center">
        {p.cols.map((c) => (
          <div key={c.iso} className="flex-1 min-w-0 flex justify-center">
            {c.hasInc && (
              <span className="min-w-[18px] h-[18px] px-[4px] rounded-[9px] bg-[#FBE3DF] text-[#9E2F24] text-[10px] font-bold flex items-center justify-center">
                {c.inc}
              </span>
            )}
          </div>
        ))}
      </div>
      <span />
      <div className="flex min-w-0">
        {p.cols.map((c) => (
          <span
            key={c.iso}
            className="flex-1 min-w-0 text-center text-[10px] whitespace-nowrap"
            style={{ color: c.dFg, fontWeight: c.dW }}
          >
            {c.date}
          </span>
        ))}
      </div>
    </div>
  </div>
);

/**
 * "Bowel pattern": a week or month of movements, day by day, with an optional
 * compared period and a summary table ("Gut Health v12", Bowel Health tab).
 *
 * @param {Object} props
 * @param {Object} props.bc - buildView().bowel.
 * @param {(iso: string) => void} props.onSelect - A day to select (also steps the period).
 * @param {(view: string) => void} props.onView
 * @param {(cmp: string) => void} props.onCompare
 */
const BowelPattern = ({ bc, onSelect, onView, onCompare }) => (
  <div className="bg-white border border-[#ECECEC] rounded-[12px] px-[22px] py-[20px] flex flex-col gap-[20px] min-w-0">
    <div className="flex flex-wrap items-center justify-between gap-x-[20px] gap-y-[12px]">
      <div className="flex flex-col gap-[2px]">
        <span className="text-[15px] font-bold">Bowel pattern</span>
        <span className="text-[12px] text-[#6b6b6b]">Are stools normal, and is it getting better?</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-[16px] gap-y-[10px]">
        <Segmented options={bc.viewOpts} onPick={onView} label="Bowel pattern period" />
        <div className="flex items-center gap-[6px]">
          <Step label="Previous period" glyph="‹" to={bc.prevSel} onPick={onSelect} />
          <span className="text-[13px] font-bold min-w-[110px] text-center whitespace-nowrap" aria-live="polite">
            {bc.periodLabel}
          </span>
          <Step label="Next period" glyph="›" to={bc.nextSel} onPick={onSelect} />
        </div>
        <div className="flex items-center gap-[8px]">
          <span className="text-[12px] font-semibold text-[#6b6b6b] whitespace-nowrap">Compare with</span>
          <Segmented options={bc.cmpOpts} onPick={onCompare} label="Bowel pattern compare with" />
        </div>
      </div>
    </div>

    {bc.panels.map((p) => (
      <Panel key={p.key} p={p} onSelect={onSelect} />
    ))}

    <div className="flex flex-wrap items-center gap-x-[18px] gap-y-[8px] text-[12px] text-[#555]">
      <span className="flex items-center gap-[4px] whitespace-nowrap">
        Bristol type
        {bc.types.map((t) => (
          <span
            key={t.label}
            className="w-[18px] h-[16px] rounded-[3px] text-[10px] font-bold flex items-center justify-center"
            style={{ background: t.bg, color: t.fg }}
          >
            {t.label}
          </span>
        ))}
      </span>
      <span className="flex items-center gap-[6px] whitespace-nowrap">
        <span className="w-[12px] h-[12px] rounded-[2px] bg-[#D99A82]" />
        Urgency avg (0–3)
      </span>
      <span className="flex items-center gap-[6px] whitespace-nowrap">
        <span className="w-[12px] h-[12px] rounded-[2px] bg-[#8E4330]" />
        Straining avg (0–3)
      </span>
      <span className="flex items-center gap-[6px] whitespace-nowrap">
        <span className="w-[12px] h-[12px] rounded-[3px] bg-white" style={{ boxShadow: "inset 0 0 0 2px #C8553D" }} />
        Red outline = incomplete
      </span>
      <span className="flex items-center gap-[6px] whitespace-nowrap">
        <span className="w-[12px] h-[12px] rounded-[3px]" style={{ boxShadow: "inset 0 0 0 1px #CFC4BB", background: HATCH }} />
        Not recorded
      </span>
    </div>

    <div className="flex flex-col border-t border-[#F1ECE8] pt-[14px]">
      <div className="grid gap-[12px] pb-[8px] border-b border-[#F1ECE8]" style={{ gridTemplateColumns: bc.gcols }}>
        <span className="text-[11px] font-bold text-[#8a8a8a] tracking-[.04em]">SUMMARY</span>
        {bc.head.map((hd) => (
          <div key={hd.t} className="flex flex-col">
            <span className="text-[12px] font-bold">{hd.t}</span>
            <span className="text-[11px] text-[#8a8a8a]">{hd.s}</span>
          </div>
        ))}
        {bc.hasCmp && <span className="text-[11px] font-bold text-[#8a8a8a] tracking-[.04em]">CHANGE</span>}
      </div>
      {bc.rows.map((r) => (
        <div key={r.k} className="grid gap-[12px] py-[10px] border-b border-[#F7F3F0] items-center" style={{ gridTemplateColumns: bc.gcols }}>
          <div className="flex flex-col">
            <span className="text-[13px] font-semibold">{r.k}</span>
            <span className="text-[11px] text-[#8a8a8a]">{r.kd}</span>
          </div>
          {r.cells.map((c, i) => (
            <div key={i} className="flex flex-col gap-[4px] min-w-0">
              <span className="text-[13px] font-bold tabular-nums">
                {c.v} <span className="text-[11px] font-normal text-[#8a8a8a]">{c.sub}</span>
              </span>
              {c.hasBar && (
                <div className="flex h-[8px] rounded-[4px] overflow-hidden bg-[#F3EEEA] max-w-[220px]">
                  {c.segs.map((g, j) => (
                    <div key={j} style={{ width: g.w, background: g.bg }} />
                  ))}
                </div>
              )}
            </div>
          ))}
          {r.hasCh && (
            <span className="text-[12px] font-bold" style={{ color: r.dFg }}>
              {r.d}
            </span>
          )}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-x-[16px] gap-y-[8px] text-[11px] text-[#6b6b6b] pt-[10px]">
        <span className="font-bold text-[#4a4a4a]">Urgency &amp; straining</span>
        {bc.levels.map((l) => (
          <span key={l.label} className="flex items-center gap-[6px] whitespace-nowrap">
            <span className="w-[10px] h-[10px] rounded-[2px]" style={{ background: l.bg }} />
            {l.label}
          </span>
        ))}
      </div>
    </div>
  </div>
);

export default BowelPattern;
